import { useCallback, useMemo } from 'react';
import useAsync from 'src/hooks/useAsync';
import { DropDownListDataItem } from 'src/types/generic';
import axiosInstance from 'src/utils/axios';

export type GLCodeObjectValue = { id: number; label: string; balance: number; initialBudgetBalance: number }

export type GlCodesObject = Record<
  string,
  GLCodeObjectValue
>;

export interface GlCode {
  id: number;
  code: string;
  description: string;
  remainingBudgetAmount: number;
  remainingInitialBudget: number;
  glApprovalStatus: 0 | 1 | 2;
}

interface GLCodeDropdownDataItem extends GlCode, DropDownListDataItem {
  remainingBudget: number,
  remainingInitialBudget: number,
}

export type GLCodeDropdownData = GLCodeDropdownDataItem[];

const useGlCodesApi = (companyId: string, glPeriodId?: number, isGlPeriodIdRequired?: boolean) => {
  const call = useCallback(async () => {
    try {
      if (!companyId) {
        return [] as GlCode[];
      }
      if (isGlPeriodIdRequired && !glPeriodId) {
        return [] as GlCode[];
      }
      const res = await axiosInstance.get<GlCode[]>('GLCode', {
        params: { companyId, glPeriodId },
      });
      return res.data;
    } catch (error) {
      throw new Error(error);
    }
  }, [companyId, glPeriodId, isGlPeriodIdRequired]);

  const { value, isLoading } = useAsync(call, true);

  const glCodeOptions: GLCodeDropdownDataItem[] = useMemo(
    () =>
      value?.map((glCode) => ({
        ...glCode,
        label: generateGlCodeDescription(glCode),
        value: glCode?.code,
        id: glCode?.id,
        remainingBudget: glCode?.remainingBudgetAmount,
        remainingInitialBudget: glCode?.remainingInitialBudget,
      })) || [],
    [value]
  );

  const glCodesObject: GlCodesObject = useMemo(
    () =>
      value?.reduce<GlCodesObject>((acc, code) => {
        acc[code.code] = {
          label: generateGlCodeDescription(code),
          id: code.id,
          balance: code.remainingBudgetAmount,
          initialBudgetBalance: code.remainingInitialBudget,
        };
        return acc;
      }, {}),
    [value]
  );

  return {
    glCodes: value || [],
    glCodeOptions,
    glCodesObject,
    areGLCodesLoading: isLoading
  };
};

export default useGlCodesApi;

export const generateGlCodeDescription = (glCode: GlCode) =>
  `${glCode?.code} - ${glCode?.description}`;
